<template>
  <v-card class="sub-agent-panel" outlined>
    <div class="sub-agent-panel-header" @click="toggleExecutions">
      <div class="sub-agent-panel-icon">
        <v-icon size="20" color="#384049">{{
          ehRespostas ? "mdi-email-check-outline" : "mdi-airplane"
        }}</v-icon>
      </div>
      <div class="sub-agent-panel-title">
        <div class="name-row">
          <span class="name">{{
            ehRespostas
              ? "Respostas da Azul por e-mail"
              : "Importação de planilhas Azul"
          }}</span>
          <StatusChip
            v-if="statusKey !== 'sucesso'"
            :status="statusKey"
            :text="statusText"
          />
        </div>
        <span class="description">{{
          ehRespostas
            ? "Leitura dos e-mails de encerramento dos chamados e correção no Phoenix"
            : "Leitura do e-mail da Azul, importação no Phoenix e arquivo no Drive"
        }}</span>
      </div>
      <v-icon size="20" class="chevron" :class="{ 'is-open': showExecutions }">
        mdi-chevron-down
      </v-icon>
    </div>

    <div class="sub-agent-panel-facts">
      <div class="fact">
        <span class="fact-label">Última execução</span>
        <span class="fact-value">{{ lastExecLabel }}</span>
      </div>
      <template v-if="!ehRespostas">
        <div v-if="ultima && ultima.data_inicial" class="fact">
          <span class="fact-label">Período importado</span>
          <span class="fact-value"
            >{{ ultima.data_inicial }} a {{ ultima.data_final }}</span
          >
        </div>
        <div v-if="ultima && ultima.nome_final" class="fact">
          <span class="fact-label">Planilha</span>
          <span class="fact-value">{{ ultima.nome_final }}</span>
        </div>
      </template>

      <div class="fact">
        <span class="fact-label">Frequência</span>
        <span class="fact-value">{{
          ehRespostas ? "Manual" : "A cada 2 horas (08:30 às 22:30)"
        }}</span>
      </div>
      <v-btn
        v-if="!ehRespostas && ultima && ultima.drive_url"
        class="azul-drive-button"
        small
        text
        color="#0f7a6e"
        :href="ultima.drive_url"
        target="_blank"
        rel="noopener"
        @click.stop
      >
        <v-icon size="16" left>mdi-google-drive</v-icon>
        Abrir no Drive
      </v-btn>
      <v-btn
        class="run-button"
        small
        :outlined="!runButtonBusy"
        :depressed="runButtonBusy"
        :disabled="disabled || runButtonBusy || outroOcupado"
        color="#0f7a6e"
        :dark="runButtonBusy"
        :title="outroOcupado ? 'Há outra execução desta base em andamento' : ''"
        @click.stop="ehRespostas ? startRespostas() : startRun()"
      >
        <v-icon size="16" left :class="{ 'spin-icon': runButtonBusy }">
          {{
            runButtonBusy
              ? "mdi-loading"
              : ehRespostas
              ? "mdi-email-check-outline"
              : "mdi-play-circle-outline"
          }}
        </v-icon>
        {{ runButtonLabel }}
      </v-btn>
    </div>

    <p v-if="runError" class="sub-agent-panel-run-error">
      <v-icon size="14" color="#c81e22">mdi-alert-circle-outline</v-icon>
      {{ runError }}
    </p>

    <!-- O e-mail de hoje da Azul ainda nao chegou: o agente parou e pergunta. -->
    <div v-if="decisao && !ehRespostas" class="azul-decision">
      <div class="azul-decision-text">
        <v-icon size="18" color="#d9741a">mdi-help-circle-outline</v-icon>
        <span>{{ decisao.aviso }}</span>
      </div>
      <div class="azul-decision-actions">
        <v-btn
          small
          depressed
          dark
          color="#d9741a"
          :disabled="disabled || runButtonBusy"
          @click="confirmOpen = true"
        >
          Importar mesmo assim
        </v-btn>
        <span class="azul-decision-hint"
          >ou aguarde o e-mail de hoje e clique em “Executar agora”.</span
        >
      </div>
    </div>

    <button class="steps-toggle" @click.stop="showSteps = !showSteps">
      <v-icon size="14">{{
        showSteps ? "mdi-eye-off-outline" : "mdi-format-list-bulleted"
      }}</v-icon>
      {{ showSteps ? "Ocultar etapas do pipeline" : "Ver etapas do pipeline" }}
    </button>
    <v-expand-transition>
      <div v-if="showSteps" class="sub-agent-panel-steps">
        <PipelineSteps :steps="passos" :current-step-index="currentStepIndex" />
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="isRunningLive" class="sub-agent-panel-live">
        <div class="live-title">
          <span class="live-badge">AO VIVO</span>
          Execução em andamento
        </div>
        <LogTimeline :logs="liveLogs" />
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div v-if="showExecutions" class="sub-agent-panel-executions">
        <div class="executions-title">Execuções no período</div>
        <p v-if="loadingExecutions" class="executions-empty">
          Carregando execuções…
        </p>
        <template v-else-if="localExecutions.length">
          <ExecutionRow
            v-for="execution in localExecutions"
            :key="execution.id"
            :execution="execution"
            :open="openExecutionId === execution.id"
            :loading="loadingLogsId === execution.id"
            @toggle="toggleExecution(execution.id)"
          />
        </template>
        <p v-else class="executions-empty">
          Nenhuma execução no período selecionado.
        </p>
      </div>
    </v-expand-transition>

    <AzulRunDialog
      v-model="runDialog"
      :targets="runTargets"
      @done="aposExecutar"
    />

    <v-dialog v-model="confirmOpen" max-width="480" persistent>
      <v-card v-if="decisao">
        <v-card-title class="period-dialog-title"
          >Importar a planilha antiga · {{ baseLabel }}?</v-card-title
        >
        <v-card-text>
          <p class="azul-confirm-text">{{ decisao.aviso }}</p>
          <p class="azul-confirm-text">
            Período:
            <strong
              >{{ decisao.data_inicial }} a {{ decisao.data_final }}</strong
            >. A planilha será importada
            <strong>mesmo que já tenha sido enviada antes</strong>.
          </p>
          <p v-if="confirmErro" class="period-dialog-error">
            {{ confirmErro }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="confirmEnviando" @click="confirmOpen = false"
            >Esperar</v-btn
          >
          <v-btn
            color="#d9741a"
            dark
            depressed
            :loading="confirmEnviando"
            @click="aceitarAntigo"
          >
            Importar mesmo assim
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import StatusChip from "@/components/StatusChip/StatusChip.vue";
import PipelineSteps from "@/views/Dashboard/Partials/PipelineSteps/PipelineSteps.vue";
import ExecutionRow from "@/views/Dashboard/Partials/ExecutionRow/ExecutionRow.vue";
import LogTimeline from "@/views/Dashboard/Partials/LogTimeline/LogTimeline.vue";
import AzulRunDialog from "./Partials/AzulRunDialog.vue";
import {
  getAzulExecutions,
  getAzulExecution,
  getAzulFila,
  runAzul,
  runAzulRespostas,
  azulErro,
} from "@/services/azulApi.js";
import {
  AZUL_STEPS,
  AZUL_STEPS_RESPOSTAS,
  stepIndexFromEtapa,
  logsParaTimeline,
  statusDoPainel,
  ehDeHoje,
} from "./azulStatus.js";

const STATUS_TEXT = {
  fila: "Na fila",
  andamento: "Em execução",
  sucesso: "Disponível",
  erro: "Erro",
  sem_email: "Sem e-mail",
  aguardando_decisao: "Aguardando decisão",
};

function hojeRange() {
  const d = new Date();
  return { start: d, end: d };
}

export default {
  name: "AzulPanel",
  components: {
    StatusChip,
    PipelineSteps,
    ExecutionRow,
    LogTimeline,
    AzulRunDialog,
  },
  props: {
    base: { type: String, required: true },
    baseLabel: { type: String, default: "" },
    range: { type: Object, required: true },
    disabled: { type: Boolean, default: false },
    // "completa" = Importação de planilhas Azul | "respostas" = Respostas da Azul por e-mail (cada uma e' um painel)
    etapa: { type: String, default: "completa" },
    // a OUTRA etapa desta base esta rodando: nao deixa disparar duas ao mesmo tempo
    outroOcupado: { type: Boolean, default: false },
  },
  data() {
    return {
      localExecutions: [],
      showExecutions: false,
      showSteps: false,
      openExecutionId: null,
      loadingLogsId: null,
      loadingExecutions: false,
      isRunningLive: false, // ha execucao 'andamento' real nesta base
      requested: false, // clicamos "Executar" e esperamos a execucao aparecer
      runError: "",
      liveLogs: [],
      runningExecId: null,
      runningEtapa: null,
      preRunExecIds: [],
      pollTimer: null,
      pollStartedAt: 0,
      runDialog: false,
      runTargets: [],
      confirmOpen: false,
      confirmEnviando: false,
      confirmErro: "",
      steps: AZUL_STEPS,
    };
  },
  computed: {
    ultima() {
      return this.localExecutions[0] || null;
    },
    statusKey() {
      // Pedido feito que ainda nao virou execucao = "Na fila" (esperando a vez); so "Em execucao" quando ha execucao aberta.
      return statusDoPainel(this.ultima, this.isRunningLive, this.requested && !this.isRunningLive);
    },
    statusText() {
      return STATUS_TEXT[this.statusKey] || this.statusKey;
    },
    lastExecLabel() {
      return this.ultima
        ? `${this.ultima.date} · ${this.ultima.start}`
        : "Nenhuma execução no período";
    },
    runButtonBusy() {
      return this.requested || this.isRunningLive;
    },
    // Este painel e' o da importacao (completa) ou o das respostas por e-mail.
    ehRespostas() {
      return this.etapa === "respostas";
    },
    passos() {
      return this.ehRespostas ? AZUL_STEPS_RESPOSTAS : AZUL_STEPS;
    },
    runButtonLabel() {
      if (this.requested && !this.isRunningLive) return "Aguardando…";
      if (this.isRunningLive) return this.ehRespostas ? "Lendo respostas…" : "Executando…";
      return "Executar agora";
    },
    currentStepIndex() {
      return this.isRunningLive ? stepIndexFromEtapa(this.runningEtapa, this.etapa) : null;
    },
    // A decisao so vale para a execucao de HOJE e enquanto nada esta rodando.
    decisao() {
      const u = this.ultima;
      if (!u || u.status !== "aguardando_decisao") return null;
      if (!ehDeHoje(u.date) || this.runButtonBusy) return null;
      return u.data_inicial && u.data_final ? u : null;
    },
  },
  watch: {
    base() {
      // Trocar a base com uma execucao sendo acompanhada ao vivo deixava o poll
      // preso na base antiga (mesmo problema que a CVC ja teve): reseta tudo.
      this.resetarAoVivo();
      this.carregar().then(this.syncLiveState);
    },
    range() {
      if (this.pollTimer) return;
      this.carregar();
    },
  },
  mounted() {
    this.carregar().then(this.syncLiveState);
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    resetarAoVivo() {
      this.stopPolling();
      this.requested = false;
      this.isRunningLive = false;
      this.runningExecId = null;
      this.runningEtapa = null;
      this.liveLogs = [];
      this.preRunExecIds = [];
      this.runError = "";
      this.openExecutionId = null;
    },
    // "Importar mesmo assim" continua a MESMA execucao: a linha "aguardando decisao" deixa de existir (o servidor a apaga
    // quando a nova chega). Enquanto a nova roda, a tela nunca mostra as duas.
    tirarSuperadas() {
      this.localExecutions = this.localExecutions.filter(
        (e) => e.status !== "aguardando_decisao"
      );
    },
    async carregar() {
      this.loadingExecutions = true;
      try {
        this.localExecutions = await getAzulExecutions(this.range, this.base, this.etapa);
        if (this.requested || this.isRunningLive) this.tirarSuperadas();

      } catch (err) {
        console.error("Falha ao carregar execuções da Azul", err);
        this.localExecutions = [];
      } finally {
        this.loadingExecutions = false;
      }
    },
    // Reconecta ao "ao vivo" se a base ja tiver uma execucao em andamento.
    async syncLiveState() {
      let execs;
      try {
        execs = await getAzulExecutions(hojeRange(), this.base, this.etapa);
      } catch (e) {
        return;
      }
      const emAndamento = execs.find((e) => e.status === "andamento");
      if (!emAndamento) {
        // Sem execucao aberta: se ha um pedido esperando a vez (feito antes, por outra aba ou depois de recarregar a pagina),
        // o painel mostra "Na fila" e acompanha ate a execucao comecar.
        try {
          const f = await getAzulFila(this.base, this.etapa);
          if (f && f.naFila && !this.requested) this.acompanharNovaExecucao();
        } catch (e) {
          /* sem a fila: o painel segue como estava */
        }
        return;
      }
      this.runningExecId = emAndamento.id;
      this.runningEtapa = emAndamento.etapa_atual;
      this.isRunningLive = true;
      await this.atualizarLogsAoVivo();
      this.startPolling();
    },
    async atualizarLogsAoVivo() {
      if (!this.runningExecId) return;
      try {
        const d = await getAzulExecution(this.runningExecId);
        this.liveLogs = logsParaTimeline(d.logs);
      } catch (e) {
        /* mantem os logs que ja tinha */
      }
    },
    toggleExecutions() {
      this.showExecutions = !this.showExecutions;
      if (!this.showExecutions) this.openExecutionId = null;
    },
    async toggleExecution(id) {
      if (this.openExecutionId === id) {
        this.openExecutionId = null;
        return;
      }
      this.openExecutionId = id;
      const exec = this.localExecutions.find((e) => e.id === id);
      if (!exec || exec.logs) return;
      this.loadingLogsId = id;
      try {
        const d = await getAzulExecution(id);
        this.$set(exec, "logs", logsParaTimeline(d.logs));
      } catch (err) {
        console.error("Falha ao carregar logs da execução", err);
        this.$set(exec, "logs", []);
      } finally {
        this.loadingLogsId = null;
      }
    },
    startRun() {
      if (this.disabled || this.runButtonBusy) return;
      this.runTargets = [{ slug: this.base, label: this.baseLabel }];
      this.runDialog = true;
    },
    // Botao "Ler respostas e corrigir": sem periodo, entra direto na fila (so le os e-mails da Azul e corrige).
    async startRespostas() {
      if (this.disabled || this.runButtonBusy) return;
      this.runError = "";
      try {
        await runAzulRespostas(this.base);
        this.acompanharNovaExecucao();
      } catch (err) {
        this.runError = azulErro(err, "Falha ao solicitar a leitura das respostas.");
      }
    },
    // O dialogo ja fez os pedidos; aqui so passamos a acompanhar a base atual.
    aposExecutar(resultados) {
      const minha = resultados.find((r) => r.slug === this.base);
      if (minha && minha.ok) this.acompanharNovaExecucao();
      else this.carregar();
    },
    acompanharNovaExecucao() {
      // Marca quais execucoes ja existiam — assim o poll reconhece a NOVA.
      this.preRunExecIds = this.localExecutions.map((e) => e.id);
      this.requested = true;
      this.showExecutions = true;
      this.$emit("run-start");
      this.startPolling();
    },
    async aceitarAntigo() {
      const u = this.decisao;
      if (!u) return;
      this.confirmEnviando = true;
      this.confirmErro = "";
      try {
        await runAzul(this.base, {
          dataInicial: u.data_inicial,
          dataFinal: u.data_final,
          aceitarAntigo: true,
        });
        this.confirmOpen = false;
        this.acompanharNovaExecucao(); // guarda os ids ja existentes (inclusive o da espera) para reconhecer a NOVA
        this.tirarSuperadas();
      } catch (err) {
        this.confirmErro = azulErro(err, "Falha ao solicitar a importação.");
      } finally {
        this.confirmEnviando = false;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollStartedAt = Date.now();
      this.pollTimer = setInterval(this.pollTick, 5000);
      this.pollTick();
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    finalizarAcompanhamento() {
      this.stopPolling();
      this.requested = false;
      this.isRunningLive = false;
      this.runningExecId = null;
      this.runningEtapa = null;
      this.liveLogs = [];
      this.$emit("run-end");
      this.$emit("changed");
      this.carregar();
    },
    async pollTick() {
      // Trava de seguranca: nao acompanha pra sempre se algo travar.
      if (Date.now() - this.pollStartedAt > 40 * 60 * 1000) {
        this.finalizarAcompanhamento();
        return;
      }
      let execs;
      try {
        // Sempre "hoje": o periodo escolhido na tela pode nem incluir a execucao nova.
        execs = await getAzulExecutions(hojeRange(), this.base, this.etapa);
      } catch (e) {
        return; // tenta de novo no proximo tick
      }
      const isNew = (e) => !this.preRunExecIds.includes(e.id);
      const alvo = this.requested
        ? execs.find(isNew)
        : execs.find((e) => e.id === this.runningExecId);

      if (alvo && alvo.status === "andamento") {
        this.tirarSuperadas();
        this.requested = false;
        this.isRunningLive = true;
        this.runningExecId = alvo.id;
        this.runningEtapa = alvo.etapa_atual;
        await this.atualizarLogsAoVivo();
        return;
      }
      if (alvo) {
        // Apareceu e ja terminou (acompanhada, ou rapida demais para pegar).
        this.finalizarAcompanhamento();
        return;
      }
      // Pedido feito e nenhuma execucao nova ainda: o poller do agente pega em
      // ate ~20 s (e ha limite de execucoes simultaneas). Espera ate 10 min.
      if (this.requested && Date.now() - this.pollStartedAt > 10 * 60 * 1000) {
        this.stopPolling();
        this.requested = false;
        this.runError =
          "Execução solicitada, mas o agente não iniciou em 10 min.";
        setTimeout(() => {
          this.runError = "";
        }, 10000);
        this.$emit("run-end");
      }
    },
  },
};
</script>

<!-- Mesmo CSS do painel da CVC: o visual fica identico. -->
<style
  src="../Dashboard/Partials/SubAgentPanel/styles.scss"
  scoped
  lang="scss"
/>

<style scoped lang="scss">
.azul-decision {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #f2ddb8;
  background: #fff7ec;
}
.azul-decision-text {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.82rem;
  line-height: 1.35;
  color: #6a4300;
}
.azul-decision-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
}
.azul-decision-hint {
  font-size: 0.74rem;
  color: #8a929b;
}
.azul-confirm-text {
  font-size: 0.88rem;
  color: #3d4650;
  margin: 0 0 10px;
}
.azul-drive-button {
  text-transform: none;
  font-weight: 600;
  letter-spacing: normal;
}
/* Os botões ficam à direita: o primeiro empurra, o seguinte cola nele. */
.azul-drive-button,
.run-button {
  margin-left: auto;
}
.azul-drive-button + .run-button {
  margin-left: 0;
}
</style>
